import { Box, List, ListItem, Stack, Typography } from '@mui/material';
import React from 'react';
import CustomTooltip from '../../shared/CustomTooltip';

/** Shown when a profile control is disabled due to permissions or policy. */
export const PROFILE_FIELD_NO_PERMISSION_MESSAGE =
    'You do not have permission to change this. Please contact your administrator.';

/**
 * Disabled inputs do not receive pointer events; wrap them so hover / tap
 * (via {@link CustomTooltip}) can explain why the field is locked.
 */
export function ProfileDisabledFieldWrap({
    disabled,
    children,
    title = PROFILE_FIELD_NO_PERMISSION_MESSAGE,
    placement = 'top',
}: {
    disabled: boolean;
    children: React.ReactNode;
    title?: React.ReactNode;
    placement?: 'top' | 'bottom' | 'left' | 'right';
}) {
    if (!disabled) {
        return <>{children}</>;
    }
    return (
        <CustomTooltip title={title} placement={placement}>
            <Box
                component='span'
                sx={{
                    display: 'block',
                    width: '100%',
                    cursor: 'not-allowed',
                }}
            >
                {children}
            </Box>
        </CustomTooltip>
    );
}

/** Small circle for Enabled (green) vs Disabled (red) in profile status UI. */
export function ProfileStatusDot({ enabled }: { enabled: boolean }) {
    return (
        <Box
            component='span'
            aria-hidden
            sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: enabled ? 'success.main' : 'error.main',
                flexShrink: 0,
                display: 'inline-block',
                mr: 0.75,
            }}
        />
    );
}

/** Dot + “Enabled” / “Disabled” for read-only profile rows. */
export function ProfileStatusIndicator({
    active,
}: {
    active: boolean | null | undefined;
}) {
    if (active !== true && active !== false) {
        return (
            <Typography variant='body1' color='text.secondary' component='span'>
                —
            </Typography>
        );
    }
    const enabled = active === true;
    return (
        <Stack direction='row' alignItems='center' spacing={0}>
            <ProfileStatusDot enabled={enabled} />
            <Typography variant='body2' color='text.primary' component='span'>
                {enabled ? 'Enabled' : 'Disabled'}
            </Typography>
        </Stack>
    );
}

export type ProfileReadOnlyFieldProps = {
    label: string;
    /** Optional node beside the label (e.g. help icon with tooltip). */
    labelAdornment?: React.ReactNode;
    children?: React.ReactNode;
    /** Shown when value is empty */
    empty?: string;
    /** Preserve newlines in string children */
    multiline?: boolean;
};

function isEmpty(value: React.ReactNode): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    return false;
}

export default function ProfileReadOnlyField({
    label,
    labelAdornment,
    children,
    empty = '—',
    multiline = false,
}: ProfileReadOnlyFieldProps) {
    // Unique per field instance so label + value can be linked for a11y / getByLabel.
    const labelId = React.useId();

    return (
        <Stack spacing={0.5} sx={{ minWidth: 0, width: '100%' }}>
            <Stack
                direction='row'
                alignItems='center'
                spacing={0.5}
                flexWrap='wrap'
                useFlexGap
            >
                <Typography
                    id={labelId}
                    variant='caption'
                    component='span'
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        letterSpacing: 0.02,
                    }}
                >
                    {label}
                </Typography>
                {labelAdornment}
            </Stack>
            <Typography
                variant='body2'
                component='div'
                aria-labelledby={labelId}
                sx={{
                    color: 'text.primary',
                    fontWeight: 400,
                    whiteSpace: multiline ? 'pre-line' : undefined,
                    wordBreak: 'break-word',
                }}
            >
                {isEmpty(children) ? empty : children}
            </Typography>
        </Stack>
    );
}

/** Same title scale as {@link ProfileSectionCard} (e.g. “Company details”). */
export function ProfileShellInlineSectionTitle({
    children,
}: {
    children: React.ReactNode;
}) {
    return (
        <Typography
            component='h3'
            variant='h6'
            sx={{
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.3,
            }}
        >
            {children}
        </Typography>
    );
}

/** Same subtitle scale as {@link ProfileSectionCard} card subtitle. */
export function ProfileShellInlineSectionSubtitle({
    children,
}: {
    children: React.ReactNode;
}) {
    return (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
            {children}
        </Typography>
    );
}

/** Label above control — same vertical rhythm as {@link ProfileReadOnlyField} for profile shell edit mode */
export function ProfileShellFormCell({
    label,
    labelAdornment,
    fieldId,
    children,
}: {
    label: React.ReactNode;
    labelAdornment?: React.ReactNode;
    fieldId?: string;
    children: React.ReactNode;
}) {
    return (
        <Stack spacing={0.5} sx={{ minWidth: 0, width: '100%' }}>
            <Stack
                direction='row'
                alignItems='center'
                spacing={0.5}
                flexWrap='wrap'
                useFlexGap
            >
                <Typography
                    variant='caption'
                    component={fieldId ? 'label' : 'span'}
                    id={fieldId ? `${fieldId}-label` : undefined}
                    htmlFor={fieldId}
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        letterSpacing: 0.02,
                    }}
                >
                    {label}
                </Typography>
                {labelAdornment}
            </Stack>
            <Box sx={{ width: '100%' }}>{children}</Box>
        </Stack>
    );
}

/** Two-line postal block: street, then suburb postcode state country */
export function formatCompanyBlockAddress(company: unknown): string {
    if (!company || typeof company !== 'object') return '';
    const c = company as Record<string, unknown>;
    const line1 = String(c.streetAddress ?? '').trim();
    const line2 = [c.suburb, c.postcode, c.state, c.countryCode]
        .filter((p) => p != null && String(p).trim() !== '')
        .map((p) => String(p).trim())
        .join(' ');
    return [line1, line2].filter(Boolean).join('\n');
}

/** Same parts as {@link formatCompanyBlockAddress}, joined for one-line inputs. */
export function formatCompanyAddressSingleLine(company: unknown): string {
    const block = formatCompanyBlockAddress(company);
    if (!block) return '';
    return block.replace(/\n/g, ', ');
}

export function companyCardAccessLabel(loggedInUser: {
    role?: string;
    viewOnly?: boolean;
}): string {
    const role = loggedInUser?.role;
    if (role === 'owner_admin') return 'Admin';
    if (role === 'owner_user') return 'User';
    if (role === 'guest_user')
        return loggedInUser?.viewOnly ? 'View Only User' : 'User';
    if (role === 'admin') return 'Admin';
    return 'Master Account';
}

/** Access role help text for profile Personal Details (varies by logged-in user type). */
export function personalAccessTooltipContent(loggedInUser: {
    role?: string;
}): React.ReactNode {
    if (loggedInUser?.role?.includes('contractor')) {
        return (
            <Typography component='div'>
                <List>
                    <ListItem>
                        Admin: Manage jobs and users within the organisation.
                    </ListItem>
                    <ListItem>
                        User: View and manage jobs assigned to them.
                    </ListItem>
                </List>
            </Typography>
        );
    }

    if (loggedInUser?.role === 'admin') {
        return (
            <Typography component='div'>
                <List>
                    <ListItem>
                        Admin: Add, edit, and view users and clients.
                    </ListItem>
                </List>
            </Typography>
        );
    }

    if (loggedInUser?.role?.includes('owner')) {
        return (
            <Typography component='div'>
                <List>
                    <ListItem>
                        Admin: Has access to view/add/edit experiences. Add and
                        manage users and approve, edit and decline reservations
                        from guests.
                    </ListItem>
                    <ListItem>
                        User: Has access to view reservations for guests, view
                        experiences and make blocked or on behalf of bookings.
                    </ListItem>
                    <ListItem>
                        View Only User: They have access to the app to see the
                        experiences and bookings but cannot edit an experience
                        or make/edit a booking.
                    </ListItem>
                </List>
            </Typography>
        );
    }

    return (
        <Typography component='div'>
            <List>
                <ListItem>
                    Company Admin: Manage reservations across the entire
                    company, including adding, editing, and viewing bookings, as
                    well as managing users and company-wide settings.
                </ListItem>
                <ListItem>
                    Team Admin: Manage reservations within your assigned team,
                    including adding, editing, and viewing bookings, as well as
                    managing team users.
                </ListItem>
                <ListItem>
                    User: Create, edit, and view their own reservations.
                </ListItem>
                <ListItem>
                    View Only User: View listings and reservations made on their
                    behalf.
                </ListItem>
            </List>
        </Typography>
    );
}

export function personalAccessLabelFromProfile(
    profile: {
        role?: string;
        viewOnly?: boolean;
    } | null,
): string {
    if (!profile?.role) return '—';
    if (profile.role === 'owner_admin') return 'Admin';
    if (profile.role === 'owner_user') return 'User';
    if (profile.role === 'contractor_admin') return 'Admin';
    if (profile.role === 'contractor_user') return 'User';
    if (profile.role === 'guest_department_admin') return 'Team Admin';
    if (profile.role === 'external_attendee_user') return 'External Attendee';
    if (profile.role === 'guest_user')
        return profile.viewOnly ? 'View Only User' : 'User';
    return 'Company Admin';
}
